/**
 * Raport zarządu — teksty sekcji logistics (`mbr.logistics.*`). WŁAŚCICIEL: agent sekcji logistics (docs/RAPORT-ZARZADU.md).
 * Podpięte już w i18n/mbr.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 * Własne kody uzgodnień sekcji: `checks: { KOD: '…' }` (panel szuka `mbr.checks.KOD`, potem `mbr.logistics.checks.KOD`).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrLogisticsPl = {
  title: 'Logistyka i flota',
  description: 'Paliwo, kilometry, godziny kierowców, koszt kilometra per auto, wykorzystanie limitów.',
  kpi: {
    fuelCost: 'Paliwo',
    km: 'Kilometry',
    kmPerOrder: 'Km na zamówienie',
    hoursPerOrder: 'Godziny kierowców na zamówienie',
    costPerKm: 'Koszt km (paliwo + najem + nadprzebieg)',
    driversOverLimit: 'Kierowcy powyżej progu godzin',
    driverHours: 'Godziny kierowców',
    hoursPerDeliveryDay: 'Godziny kierowców na dzień dostaw',
    fuelPerKm: 'Paliwo na km',
    rentCost: 'Najem i leasing aut',
    excessMileageCost: 'Nadprzebieg (naliczenie)',
    vehiclesRented: 'Auta w najmie',
    vehiclesNotRented: 'Auta bez najmu',
  },
  checks: {
    LOGISTICS_KM_SUM:
      'Km w podsumowaniu logistyki ({reported}) różnią się od sumy km aut ({computed}) o {diff} km.',
    LOGISTICS_FUEL_SUM:
      'Paliwo w podsumowaniu ({expected}) różni się od sumy paliwa aut ({actual}) o {diff}.',
    LOGISTICS_HOURS_SUM:
      'Godziny kierowców w podsumowaniu ({reported}) różnią się od sumy godzin kierowców ({computed}) o {diff} h.',
    LOGISTICS_VEHICLE_COUNT:
      'Liczba aut w podsumowaniu ({reported}) różni się od liczby aut w najmie na liście aut ({computed}).',
  },
  overview: {
    title: 'Wskaźniki logistyki',
    description: 'Bieżący okres i okres bazowy; kolor = ocena dla firmy (koszt w górę = źle).',
  },
  trend: {
    title: 'Kilometry i godziny kierowców — 12 miesięcy',
    description: 'Słupki: km (lewa oś), linia: godziny kierowców (prawa oś); miesiąc raportu wyróżniony.',
    km: 'Kilometry',
    hours: 'Godziny kierowców',
    empty: 'Brak danych do trendu.',
  },
  units: { km: '{value} km', h: '{value} h' },
  charts: {
    costKm: {
      title: 'Koszt km per auto',
      subtitle: 'Najdroższe auta logistyki; kolor względem średniego kosztu km floty.',
      value: 'koszt km',
      dear: '≥ 1,4× średniej',
      dearest: '≥ 2× średniej',
      average: 'średnia floty {value}',
      empty: 'Brak kosztów km w tym okresie.',
    },
    driverHours: {
      title: 'Godziny kierowców względem progu',
      subtitle: 'Najbardziej obciążeni kierowcy w miesiącu raportu.',
      value: 'godziny w miesiącu',
      over: 'ponad próg',
      threshold: 'próg {limit} h',
    },
  },
  otherVehicles: {
    title: 'Auta poza logistyką',
    description:
      'Pojazdy spoza typów logistyki w ustawieniach raportu (domyślnie osobowe auta handlowców). Nie wchodzą do km, paliwa, liczby aut ani godzin kierowców; ranking kosztu km i pary zamiany liczone osobno.',
  },
  vehicles: {
    title: 'Flota per auto',
    description:
      'Pojazdy logistyki (typy z ustawień raportu). Koszt kilometra = (paliwo + najem + nadprzebieg) / km. Wykorzystanie = km / limit miesięczny umowy.',
    count: '{count} aut',
    filter: { all: 'Wszystkie', flagged: 'Do przesunięcia', rented: 'W najmie', other: 'Poza logistyką' },
    total: 'Razem',
    base: 'bazowo {value}',
    limit: 'limit {value}',
    open: 'Otwórz kartę pojazdu {plate}',
    costScale:
      'Kolor kosztu km względem średniej grupy ważonej kilometrami ({value}): czerwony ≥ 2×, żółty ≥ 1,4×, zielony ≤ 0,8×.',
    flagHint: {
      UNDERUSED:
        'Auto w najmie poniżej {pct}% limitu — do przesunięcia na inne trasy albo do oddania po umowie.',
      OVER_LIMIT: 'Auto ponad limit km umowy — część kursów warto przenieść na auto z zapasem.',
    },
    ai: 'Wniosek AI dotyczy tego auta',
    col: {
      rank: '#',
      plate: 'Auto',
      branch: 'Oddział',
      km: 'Km',
      kmBase: 'Km bazowo',
      utilization: 'Wykorzystanie limitu',
      fuel: 'Paliwo',
      rent: 'Najem',
      excess: 'Nadprzebieg',
      costPerKm: 'Koszt km',
      flag: 'Ocena',
    },
    flag: { UNDERUSED: 'Niski przebieg', OVER_LIMIT: 'Nadprzebieg' },
    source: { FLEET: 'z modułu Flota', MBR: 'z importu / formularza', MIXED: 'Flota + import' },
    rented: 'najem',
    noLimit: 'bez limitu',
    empty: 'Brak aut z danymi w tym okresie.',
    emptyFlagged: 'Żadne auto nie wymaga przesunięcia.',
  },
  swaps: {
    title: 'Pary do zamiany',
    description:
      'Auto z nadprzebiegiem przejmuje trasy auta z niskim przebiegiem (najpierw w tym samym oddziale).',
    pair: '{over} ↔ {under}',
    shift: 'przenieść ok. {km} km',
    saving: 'oszczędność ok. {value}',
    savingMonth: '{value} / mies.',
    km: '{value} km',
    savingLabel: 'oszczędność',
    shiftLabel: 'do przeniesienia',
    noSaving: 'bez stawki nadprzebiegu — kwoty nie da się policzyć',
    overRole: 'ponad limit',
    underRole: 'z zapasem km',
    utilization: '{value} limitu',
    empty: 'Brak par — żadne auto nie przekracza limitu albo brak aut z zapasem.',
  },
  drivers: {
    title: 'Kierowcy',
    description:
      'Godziny z kart drogowych, a bez kart — z tabeli godzin (import / formularz). L4 i urlop z tabeli godzin.',
    alert: '{count} kierowców powyżej {limit} h w miesiącu',
    noAlert: 'Nikt nie przekroczył {limit} h w miesiącu',
    masked: 'Kierowca {index}',
    maskedHint: 'Imiona i nazwiska widzą osoby z uprawnieniem do kadr albo do kart drogowych floty.',
    absenceHidden:
      'L4 i urlop per osoba widzi tylko kadra (uprawnienie kadrowe). Razem: L4 {sick} dni, urlop {vacation} dni.',
    col: {
      name: 'Kierowca',
      hours: 'Godziny',
      hoursBase: 'Bazowo',
      overtime: 'Nadgodziny',
      sick: 'L4 (dni)',
      vacation: 'Urlop (dni)',
      maxMonth: 'Maks. w miesiącu',
      monthsOver: 'Mies. > progu',
      source: 'Źródło',
    },
    source: { LOGBOOK: 'karty', MBR: 'tabela', MIXED: 'karty + tabela' },
    overLimit: 'powyżej progu',
    overBy: '+{value} h ponad próg',
    threshold: 'próg {limit} h',
    total: 'Razem',
    ai: 'Wniosek AI: kierowca ponad progiem godzin',
    empty: 'Brak godzin kierowców w tym okresie.',
  },
  costsHidden: 'Koszty floty widzi zarząd i finanse (uprawnienie do kosztów raportu).',
};

export const mbrLogisticsEn: Shape<typeof mbrLogisticsPl> = {
  title: 'Logistics & fleet',
  description: 'Fuel, kilometres, driver hours, cost per km per vehicle, limit utilisation.',
  kpi: {
    fuelCost: 'Fuel',
    km: 'Kilometres',
    kmPerOrder: 'Km per order',
    hoursPerOrder: 'Driver hours per order',
    costPerKm: 'Cost per km (fuel + rent + excess mileage)',
    driversOverLimit: 'Drivers over the hours threshold',
    driverHours: 'Driver hours',
    hoursPerDeliveryDay: 'Driver hours per delivery day',
    fuelPerKm: 'Fuel per km',
    rentCost: 'Vehicle rent & leasing',
    excessMileageCost: 'Excess mileage (accrual)',
    vehiclesRented: 'Rented vehicles',
    vehiclesNotRented: 'Vehicles without rent',
  },
  checks: {
    LOGISTICS_KM_SUM:
      'Logistics summary km ({reported}) differ from the sum of vehicle km ({computed}) by {diff} km.',
    LOGISTICS_FUEL_SUM:
      'Summary fuel ({expected}) differs from the sum of vehicle fuel ({actual}) by {diff}.',
    LOGISTICS_HOURS_SUM:
      'Summary driver hours ({reported}) differ from the sum of driver hours ({computed}) by {diff} h.',
    LOGISTICS_VEHICLE_COUNT:
      'Summary vehicle count ({reported}) differs from the rented vehicles on the vehicle list ({computed}).',
  },
  overview: {
    title: 'Logistics indicators',
    description: 'Current and base period; colour = assessment for the company (cost up = bad).',
  },
  trend: {
    title: 'Kilometres and driver hours — 12 months',
    description: 'Bars: km (left axis), line: driver hours (right axis); the report month highlighted.',
    km: 'Kilometres',
    hours: 'Driver hours',
    empty: 'No data for the trend.',
  },
  units: { km: '{value} km', h: '{value} h' },
  charts: {
    costKm: {
      title: 'Cost per km per vehicle',
      subtitle: 'The dearest logistics vehicles; colour vs the fleet average cost per km.',
      value: 'cost per km',
      dear: '≥ 1.4× the average',
      dearest: '≥ 2× the average',
      average: 'fleet average {value}',
      empty: 'No cost per km in this period.',
    },
    driverHours: {
      title: 'Driver hours vs the threshold',
      subtitle: 'The most loaded drivers in the report month.',
      value: 'hours in the month',
      over: 'over the threshold',
      threshold: 'threshold {limit} h',
    },
  },
  otherVehicles: {
    title: 'Vehicles outside logistics',
    description:
      'Vehicles of types outside logistics in the report settings (by default the sales reps’ passenger cars). Not in km, fuel, vehicle count or driver hours; cost-per-km ranking and swap pairs are separate.',
  },
  vehicles: {
    title: 'Fleet per vehicle',
    description:
      'Logistics vehicles (types from the report settings). Cost per km = (fuel + rent + excess mileage) / km. Utilisation = km / monthly contract limit.',
    count: '{count} vehicles',
    filter: { all: 'All', flagged: 'To be moved', rented: 'Rented', other: 'Outside logistics' },
    total: 'Total',
    base: 'base {value}',
    limit: 'limit {value}',
    open: 'Open the vehicle card {plate}',
    costScale:
      'Cost-per-km colour vs the km-weighted group average ({value}): red ≥ 2×, amber ≥ 1.4×, green ≤ 0.8×.',
    flagHint: {
      UNDERUSED:
        'Rented vehicle below {pct}% of its limit — move it to other routes or return it at the end of the contract.',
      OVER_LIMIT: 'Vehicle over its contract km limit — move some routes to a vehicle with spare km.',
    },
    ai: 'An AI insight concerns this vehicle',
    col: {
      rank: '#',
      plate: 'Vehicle',
      branch: 'Branch',
      km: 'Km',
      kmBase: 'Base km',
      utilization: 'Limit utilisation',
      fuel: 'Fuel',
      rent: 'Rent',
      excess: 'Excess',
      costPerKm: 'Cost per km',
      flag: 'Assessment',
    },
    flag: { UNDERUSED: 'Low mileage', OVER_LIMIT: 'Excess mileage' },
    source: { FLEET: 'from the Fleet module', MBR: 'from import / form', MIXED: 'Fleet + import' },
    rented: 'rent',
    noLimit: 'no limit',
    empty: 'No vehicles with data in this period.',
    emptyFlagged: 'No vehicle needs to be moved.',
  },
  swaps: {
    title: 'Swap pairs',
    description: 'A vehicle over its limit takes over the routes of a low-mileage one (same branch first).',
    pair: '{over} ↔ {under}',
    shift: 'shift ~{km} km',
    saving: 'saving ~{value}',
    savingMonth: '{value} / month',
    km: '{value} km',
    savingLabel: 'saving',
    shiftLabel: 'to shift',
    noSaving: 'no excess-mileage rate — the amount cannot be computed',
    overRole: 'over the limit',
    underRole: 'spare km',
    utilization: '{value} of the limit',
    empty: 'No pairs — no vehicle is over its limit or none has spare km.',
  },
  drivers: {
    title: 'Drivers',
    description:
      'Hours from logbooks; without logbooks from the hours table (import / form). Sick leave and vacation from the hours table.',
    alert: '{count} drivers above {limit} h in the month',
    noAlert: 'Nobody exceeded {limit} h in the month',
    masked: 'Driver {index}',
    maskedHint: 'Names are visible to people with HR or Fleet logbook rights.',
    absenceHidden:
      'Sick leave and vacation per person are HR-only. Total: sick leave {sick} days, vacation {vacation} days.',
    col: {
      name: 'Driver',
      hours: 'Hours',
      hoursBase: 'Base',
      overtime: 'Overtime',
      sick: 'Sick leave (days)',
      vacation: 'Vacation (days)',
      maxMonth: 'Max in a month',
      monthsOver: 'Months > limit',
      source: 'Source',
    },
    source: { LOGBOOK: 'logbooks', MBR: 'table', MIXED: 'logbooks + table' },
    overLimit: 'over the limit',
    overBy: '+{value} h over the threshold',
    threshold: 'threshold {limit} h',
    total: 'Total',
    ai: 'AI insight: driver over the hours threshold',
    empty: 'No driver hours in this period.',
  },
  costsHidden: 'Fleet costs are visible to the board and finance (report cost permission).',
};
